import type { Extension } from '@medplum/fhirtypes';
import { z } from 'zod';

import { ELUVE_FHIR_NAMESPACE } from './constants';

export const REASSESSMENT_MARKER_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/reassessment-marker`;

type ReassessmentMarkerBase = {
  bookedManually: boolean;
};

export type ReassessmentMarker = ReassessmentMarkerBase &
  (
    | { afterVisits: number; afterDays?: never }
    | { afterVisits?: never; afterDays: number }
  );

/** The marker as an instance-spec field, so a seeded or extracted reassessment
 *  point survives the spec round-trip instead of being re-derived on activation.
 *  Exactly one of afterVisits/afterDays, mirroring `ReassessmentMarker`. */
export const reassessmentMarkerSchema = z.union([
  z.object({
    afterVisits: z.number().int().min(1),
    bookedManually: z.boolean(),
  }),
  z.object({
    afterDays: z.number().int().min(1),
    bookedManually: z.boolean(),
  }),
]);

export function reassessmentMarkerExtension(
  marker: ReassessmentMarker,
): Extension {
  return {
    url: REASSESSMENT_MARKER_EXTENSION_URL,
    extension: [
      marker.afterVisits !== undefined
        ? { url: 'afterVisits', valueInteger: marker.afterVisits }
        : { url: 'afterDays', valueInteger: marker.afterDays },
      { url: 'bookedManually', valueBoolean: marker.bookedManually },
    ],
  };
}

export function reassessmentMarkerOf(resource: {
  extension?: Extension[];
}): ReassessmentMarker | undefined {
  const ext = resource.extension?.find(
    (e) => e.url === REASSESSMENT_MARKER_EXTENSION_URL,
  );
  if (!ext) return undefined;
  const sub = (url: string) => ext.extension?.find((e) => e.url === url);
  const afterVisits = sub('afterVisits')?.valueInteger;
  const afterDays = sub('afterDays')?.valueInteger;
  const bookedManually = sub('bookedManually')?.valueBoolean ?? false;
  if (afterVisits !== undefined) {
    return afterDays === undefined
      ? { afterVisits, bookedManually }
      : undefined;
  }
  return afterDays !== undefined ? { afterDays, bookedManually } : undefined;
}

export function withReassessmentMarker<T extends { extension?: Extension[] }>(
  resource: T,
  marker: ReassessmentMarker,
): T {
  return {
    ...resource,
    extension: [
      ...withoutReassessmentMarker(resource).extension,
      reassessmentMarkerExtension(marker),
    ],
  };
}

export function withoutReassessmentMarker<
  T extends { extension?: Extension[] },
>(resource: T): T & { extension: Extension[] } {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter(
      (e) => e.url !== REASSESSMENT_MARKER_EXTENSION_URL,
    ),
  };
}
